import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { Button } from '@shared/design-system/ui'
import { useMemberMeQuery } from '../api/member-access.queries'
import { MEMBER_PREVIEW_STUDENT_NAME } from '../model/preview-student'
import { useMemberSession } from '../model/member-session-store'
import {
  brand,
  footer,
  header,
  headerBack,
  headerSide,
  logo,
  main,
  memberIdentity,
  mainPlayer,
  memberAccountLink,
  memberAvatar,
  memberAvatarFallback,
  memberName,
  shell,
  shellPlayer,
} from './member-access.css'

type MemberAccessLayoutProps = {
  producerName: string
  logoUrl?: string | null
  variant?: 'page' | 'player'
  catalogBackProducerId?: string
  previewBackProductId?: string
  preview?: boolean
  previewStudentName?: string
  children: ReactNode
}

function brandMark(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  const text = words.map((word) => word[0]?.toUpperCase() ?? '').join('')
  return text || 'VP'
}

export function MemberAccessLayout({
  producerName,
  logoUrl,
  variant = 'page',
  catalogBackProducerId,
  previewBackProductId,
  preview = false,
  previewStudentName = MEMBER_PREVIEW_STUDENT_NAME,
  children,
}: MemberAccessLayoutProps) {
  const member = useMemberSession((state) => state.member)
  const clear = useMemberSession((state) => state.clear)
  const me = useMemberMeQuery(Boolean(member) && variant !== 'player' && !preview)
  const player = variant === 'player'
  const displayName = preview ? previewStudentName : (me.data?.name ?? member?.name)
  const avatarUrl = preview ? undefined : me.data?.avatarUrl
  return (
    <div className={player ? `${shell} ${shellPlayer}` : shell}>
      {player ? null : (
        <header className={header}>
          <div className={brand}>
            {catalogBackProducerId ? (
              <Link
                className={headerBack}
                to="/acesso/$producerId/biblioteca"
                params={{ producerId: catalogBackProducerId }}
                aria-label="Voltar ao catálogo"
                data-testid="member-catalog-back"
              >
                <ArrowLeft size={18} />
              </Link>
            ) : previewBackProductId ? (
              <Link
                className={headerBack}
                to="/area-membros/preview/$productId"
                params={{ productId: previewBackProductId }}
                aria-label="Voltar ao catálogo"
                data-testid="member-catalog-back"
              >
                <ArrowLeft size={18} />
              </Link>
            ) : null}
            {logoUrl ? <img className={logo} src={logoUrl} alt={producerName} /> : null}
          </div>
          {preview ? (
            <div className={headerSide}>
              <span className={memberIdentity} data-testid="member-preview-identity">
                <span className={memberAvatarFallback} aria-hidden>
                  {brandMark(previewStudentName)}
                </span>
                <span className={memberName}>{previewStudentName}</span>
              </span>
            </div>
          ) : member ? (
            <div className={headerSide}>
              <Link
                className={memberAccountLink}
                to="/acesso/$producerId/conta"
                params={{ producerId: member.producerId }}
                data-testid="member-account-link"
              >
                {avatarUrl ? (
                  <img className={memberAvatar} src={avatarUrl} alt="" />
                ) : (
                  <span className={memberAvatarFallback} aria-hidden>
                    {brandMark(displayName ?? member.name)}
                  </span>
                )}
                <span className={memberName}>{displayName}</span>
              </Link>
              <Button size="sm" variant="secondary" onClick={() => clear()}>
                Sair
              </Button>
            </div>
          ) : null}
        </header>
      )}
      <main className={player ? mainPlayer : main}>{children}</main>
      {player ? null : <footer className={footer}>Entregue pela VendePay</footer>}
    </div>
  )
}
